import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "../../../utils/translation";
import {
  listBusinesses,
  selectBusinesses,
  selectBusinessesError,
  selectBusinessesLoading,
  selectBusinessesMessage,
  selectBusinessesUpdateError,
  selectBusinessesUpdateMessage,
  setBusinessActiveOptimistic,
  updateBusinessActive,
} from "../businessSlice";

const PAGE_SIZE = 10;

const BusinessListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const businesses = useSelector(selectBusinesses);
  const isLoading = useSelector(selectBusinessesLoading);
  const isError = useSelector(selectBusinessesError);
  const message = useSelector(selectBusinessesMessage);
  const updateError = useSelector(selectBusinessesUpdateError);
  const updateMessage = useSelector(selectBusinessesUpdateMessage);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const updateLoading = useSelector((state) => state.business?.updateLoading);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [permissionError, setPermissionError] = useState("");

  useEffect(() => {
    dispatch(listBusinesses());
  }, [dispatch]);

  const getName = (biz) => {
    return biz?.businessName || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return businesses || [];
    return (businesses || []).filter((biz) =>
      (biz?.businessName || "").toLowerCase().includes(term)
    );
  }, [businesses, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString("tr-TR");
    }
    if (typeof value === "number") {
      return new Date(value).toLocaleString("tr-TR");
    }
    if (typeof value === "string") return value;
    return "-";
  };

  const valueOrDash = (value) => {
    if (value === null || value === undefined) return "-";
    const text = String(value).trim();
    return text ? text : "-";
  };

  const renderRows = () => {
    if (isLoading && (!businesses || businesses.length === 0)) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!businesses || businesses.length === 0)) {
      return (
        <tr>
          <td colSpan="4" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!paged || paged.length === 0) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return paged.map((biz) => (
      <tr
        key={biz.id || biz.businessId}
        style={{ cursor: "pointer" }}
        onClick={() => setSelectedBusiness(biz)}
      >
        <td>{highlightMatch(getName(biz), search.trim())}</td>
        <td>{biz?.businessMail || "-"}</td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id={`business-active-${biz.id || biz.businessId}`}
                checked={Boolean(biz?.businessState)}
                onChange={() => {
                  if (!adminAccess) {
                    setPermissionError(t("admin_common.permission_denied"));
                    return;
                  }
                  setPermissionError("");
                  const id = biz.id || biz.businessId;
                  if (!id) return;
                  const nextValue = !biz?.businessState;
                  dispatch(
                    setBusinessActiveOptimistic({
                      id,
                      businessState: nextValue,
                      updatedAt: Date.now(),
                    })
                  );
                  dispatch(
                    updateBusinessActive({
                      id,
                      businessState: nextValue,
                      prevBusinessState: biz?.businessState,
                    })
                  );
                }}
                disabled={updateLoading}
              />
            </div>
            <span>{biz?.businessState ? t("admin_common.status_active") : t("admin_common.status_passive")}</span>
          </div>
        </td>
        <td>{formatDate(biz?.createdAt)}</td>
      </tr>
    ));
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">İşletme Yönetimi</h4>
            {permissionError && (
              <div className="alert alert-danger">{permissionError}</div>
            )}
            {updateError && (
              <div className="text-danger mb-2">
                {updateMessage || t("admin_business_pages.update_failed")}
              </div>
            )}
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Link to="/business-management/list" className="btn btn-primary">
                {t("admin_business.buttons.list")}
              </Link>
              <Link
                to="/business-management/active"
                className="btn btn-outline-primary"
                style={{ backgroundColor: "#7EC8FF", borderColor: "#7EC8FF", color: "#ffffff" }}
              >
                {t("admin_business.buttons.active")}
              </Link>
              <Link
                to="/business-management/create"
                className="btn btn-success"
                style={{ backgroundColor: "#D97ADF", borderColor: "#D97ADF", color: "#ffffff" }}
              >
                {t("admin_business.buttons.create")}
              </Link>
              <Link
                to="/business-management/edit"
                className="btn btn-success"
                style={{ backgroundColor: "#A8E06E", borderColor: "#A8E06E", color: "#ffffff" }}
              >
                {t("admin_business.buttons.edit")}
              </Link>
              <Link
                to="/business-management/assign-users"
                className="btn btn-warning"
                style={{ backgroundColor: "#F4B740", borderColor: "#F4B740", color: "#ffffff" }}
              >
                {t("admin_business.buttons.assign_users")}
              </Link>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("admin_common.search_name_placeholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>{t("admin_common.business")}</th>
                    <th>{t("admin_common.email")}</th>
                    <th>{t("admin_common.status")}</th>
                    <th>{t("admin_common.created_at")}</th>
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-muted">
                {t("admin_common.total_records", { count: filtered.length })}
              </span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t("admin_common.prev")}
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  {currentPage} / {totalPages}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t("admin_common.next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedBusiness && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content" style={{ maxHeight: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title">İşletme Detayı</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedBusiness(null)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body" style={{ overflowY: "auto" }}>
                <div className="row">
                  <div className="col-md-6 mb-3"><strong>Business ID:</strong> {valueOrDash(selectedBusiness?.businessId || selectedBusiness?.id)}</div>
                  <div className="col-md-6 mb-3"><strong>Durum:</strong> {selectedBusiness?.businessState ? t("admin_common.status_active") : t("admin_common.status_passive")}</div>
                  <div className="col-md-6 mb-3"><strong>İşletme:</strong> {valueOrDash(selectedBusiness?.businessName)}</div>
                  <div className="col-md-6 mb-3"><strong>E-posta:</strong> {valueOrDash(selectedBusiness?.businessMail)}</div>
                  <div className="col-md-6 mb-3"><strong>Telefon:</strong> {valueOrDash(selectedBusiness?.businessPhone || selectedBusiness?.phoneNumber)}</div>
                  <div className="col-md-6 mb-3"><strong>Web:</strong> {valueOrDash(selectedBusiness?.businessWebSite || selectedBusiness?.website)}</div>
                  <div className="col-md-6 mb-3"><strong>Ülke:</strong> {valueOrDash(selectedBusiness?.country)}</div>
                  <div className="col-md-6 mb-3"><strong>Şehir:</strong> {valueOrDash(selectedBusiness?.city)}</div>
                  <div className="col-md-6 mb-3"><strong>Lokasyon:</strong> {valueOrDash(selectedBusiness?.location)}</div>
                  <div className="col-md-6 mb-3"><strong>Oluşturma:</strong> {formatDate(selectedBusiness?.createdAt || selectedBusiness?.createDate)}</div>
                  <div className="col-12 mb-3"><strong>Açıklama:</strong> {valueOrDash(selectedBusiness?.about)}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setSelectedBusiness(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessListPage;
